import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { employeeApi, departmentApi } from '../../services/api';
import { PageHeader, BandBadge, Spinner, EmptyState, Modal } from '../../components/shared/index';
import { useAuth } from '../../context/AuthContext';
import toast from 'react-hot-toast';
import { PlusIcon, MagnifyingGlassIcon, ArrowUpTrayIcon, BanknotesIcon } from '@heroicons/react/24/outline';

const ROLES  = ['manager','project_manager','developer','designer','qa','sales','cs','hr','operations','other'];
const BANDS  = ['junior','mid','senior','staff','principal','manager','director','vp','c_level'];
const STATUS = ['active','inactive','on_leave','terminated'];

function EmployeeForm({ initial, departments, onSave, onClose }) {
  const [form, setForm] = useState(initial || { name:'',email:'',role:'developer',band:'mid',job_title:'',department_id:'',hire_date:'' });
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  return (
    <form onSubmit={e => { e.preventDefault(); onSave(form); }} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div><label className="label">Full name *</label><input required className="input" value={form.name} onChange={set('name')} /></div>
        <div><label className="label">Email *</label><input required type="email" className="input" value={form.email} onChange={set('email')} /></div>
        <div><label className="label">Role *</label>
          <select required className="input" value={form.role} onChange={set('role')}>
            {ROLES.map(r => <option key={r} value={r}>{r.replace('_',' ')}</option>)}
          </select>
        </div>
        <div><label className="label">Band *</label>
          <select required className="input" value={form.band} onChange={set('band')}>
            {BANDS.map(b => <option key={b} value={b}>{b}</option>)}
          </select>
        </div>
        <div><label className="label">Job title</label><input className="input" value={form.job_title} onChange={set('job_title')} /></div>
        <div><label className="label">Department</label>
          <select className="input" value={form.department_id} onChange={set('department_id')}>
            <option value="">— None —</option>
            {departments?.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
          </select>
        </div>
        <div><label className="label">Hire date</label><input type="date" className="input" value={form.hire_date} onChange={set('hire_date')} /></div>
        <div><label className="label">Base salary</label><input type="number" className="input" value={form.base_salary||''} onChange={set('base_salary')} placeholder="Annual" /></div>
      </div>
      <div className="flex justify-end gap-3 pt-2">
        <button type="button" onClick={onClose} className="btn-secondary">Cancel</button>
        <button type="submit" className="btn-primary">Save employee</button>
      </div>
    </form>
  );
}

function ImportModal({ onClose, onDone }) {
  const [file, setFile]       = useState(null);
  const [dragging, setDrag]   = useState(false);
  const [uploading, setUpload]= useState(false);

  const handleFile = f => {
    if (!f) return;
    if (!/\.csv$/i.test(f.name)) { toast.error('Upload a .csv file'); return; }
    setFile(f);
  };

  const submit = async () => {
    if (!file) return;
    setUpload(true);
    const form = new FormData();
    form.append('file', file);
    try {
      await employeeApi.import(form);
      toast.success('Import queued — employees will appear shortly');
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Import failed');
    } finally { setUpload(false); }
  };

  return (
    <div className="space-y-4">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
        Upload a CSV with columns: <code>name, email, role, band, job_title, department, manager_email, hire_date, base_salary</code>. Duplicates (by email) will be updated.
      </div>
      <div
        onDragOver={e => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={e => { e.preventDefault(); setDrag(false); handleFile(e.dataTransfer.files[0]); }}
        className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors ${dragging ? 'border-blue-500 bg-blue-50' : 'border-gray-300 bg-gray-50'}`}
      >
        <ArrowUpTrayIcon className="w-8 h-8 mx-auto text-gray-400 mb-2" />
        <p className="text-sm text-gray-700">{file ? file.name : 'Drop your CSV here or'}</p>
        <label className="mt-2 inline-block btn-secondary btn-sm cursor-pointer">
          Browse
          <input type="file" accept=".csv,text/csv" className="hidden" onChange={e => handleFile(e.target.files[0])} />
        </label>
      </div>
      <div className="flex justify-end gap-3">
        <button onClick={onClose} className="btn-secondary">Cancel</button>
        <button disabled={!file || uploading} onClick={submit} className="btn-primary disabled:opacity-40">
          {uploading ? 'Uploading…' : 'Import'}
        </button>
      </div>
    </div>
  );
}

export default function EmployeeList() {
  const { isHR }     = useAuth();
  const qc           = useQueryClient();
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState({ status: '', department: '', role: '' });
  const [showForm, setShowForm]     = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [editing, setEditing]       = useState(null);
  const [page, setPage]             = useState(1);

  const { data, isLoading } = useQuery({
    queryKey: ['employees', page, search, filters],
    queryFn:  () => employeeApi.list({ page, search, ...filters, per_page: 20 }).then(r => r.data),
    keepPreviousData: true,
  });

  const { data: depts } = useQuery({
    queryKey: ['departments'],
    queryFn:  () => departmentApi.list().then(r => r.data),
  });

  const createMut = useMutation({
    mutationFn: employeeApi.create,
    onSuccess:  () => { qc.invalidateQueries(['employees']); setShowForm(false); toast.success('Employee added'); },
    onError:    (e) => toast.error(e.response?.data?.message || 'Failed to create'),
  });

  const updateMut = useMutation({
    mutationFn: ({ id, data }) => employeeApi.update(id, data),
    onSuccess:  () => { qc.invalidateQueries(['employees']); setEditing(null); toast.success('Updated'); },
    onError:    (e) => toast.error(e.response?.data?.message || 'Failed to update'),
  });

  const employees = data?.data || [];
  const meta      = data?.meta;

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <PageHeader
        title="Employees"
        subtitle={meta ? `${meta.total} total` : ''}
        actions={
          isHR && (
            <div className="flex gap-2">
              <button onClick={() => employeeApi.sync().then(() => toast.success('Sync queued'))} className="btn-secondary btn-sm">
                Sync HRMS
              </button>
              <button onClick={() => setShowImport(true)} className="btn-secondary">
                <ArrowUpTrayIcon className="w-4 h-4" /> Import CSV
              </button>
              <button onClick={() => setShowForm(true)} className="btn-primary">
                <PlusIcon className="w-4 h-4" /> Add employee
              </button>
            </div>
          )
        }
      />

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-5">
        <div className="relative flex-1 min-w-56">
          <MagnifyingGlassIcon className="w-4 h-4 absolute left-3 top-2.5 text-gray-400" />
          <input
            placeholder="Search name or email…"
            className="input pl-9"
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }}
          />
        </div>
        <select className="input w-36" value={filters.status} onChange={e => setFilters(f => ({ ...f, status: e.target.value }))}>
          <option value="">All statuses</option>
          {STATUS.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <select className="input w-40" value={filters.role} onChange={e => setFilters(f => ({ ...f, role: e.target.value }))}>
          <option value="">All roles</option>
          {ROLES.map(r => <option key={r} value={r}>{r.replace('_',' ')}</option>)}
        </select>
        <select className="input w-44" value={filters.department} onChange={e => setFilters(f => ({ ...f, department: e.target.value }))}>
          <option value="">All departments</option>
          {depts?.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
        </select>
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="flex justify-center py-20"><Spinner /></div>
      ) : employees.length === 0 ? (
        <EmptyState title="No employees found" description="Add your first employee to get started."
          action={isHR && <button onClick={() => setShowForm(true)} className="btn-primary">Add employee</button>} />
      ) : (
        <>
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  {['Name','Role','Band','Department','Manager','Status',''].map(h => (
                    <th key={h} className="th">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {employees.map(emp => (
                  <tr key={emp.id} className="tr-hover">
                    <td className="td">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-blue-100 text-blue-700 rounded-full flex items-center justify-center text-xs font-semibold shrink-0">
                          {emp.name[0].toUpperCase()}
                        </div>
                        <div>
                          <Link to={`/employees/${emp.id}`} className="font-medium text-gray-900 hover:text-blue-600">
                            {emp.name}
                          </Link>
                          <p className="text-xs text-gray-400">{emp.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="td capitalize">{emp.role?.replace('_', ' ')}</td>
                    <td className="td capitalize">{emp.band}</td>
                    <td className="td">{emp.department?.name || '—'}</td>
                    <td className="td">{emp.manager?.name || '—'}</td>
                    <td className="td">
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                        emp.status === 'active' ? 'bg-green-100 text-green-700'
                        : emp.status === 'terminated' ? 'bg-red-100 text-red-700'
                        : 'bg-gray-100 text-gray-600'
                      }`}>{emp.status}</span>
                    </td>
                    <td className="td">
                      <div className="flex items-center gap-3">
                        {isHR && (
                          <button onClick={() => setEditing(emp)} className="text-xs text-blue-600 hover:underline">Edit</button>
                        )}
                        {isHR && (
                          <Link
                            to={`/app/employees/${emp.id}/payroll`}
                            className="inline-flex items-center gap-1 text-xs text-blue-600 hover:underline"
                            title="Tax profile, deductions, bank accounts"
                          >
                            <BanknotesIcon className="w-3.5 h-3.5" /> Payroll
                          </Link>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {meta && meta.last_page > 1 && (
            <div className="flex justify-between items-center mt-4 text-sm text-gray-600">
              <span>Showing {meta.from}–{meta.to} of {meta.total}</span>
              <div className="flex gap-2">
                <button disabled={page === 1} onClick={() => setPage(p => p - 1)} className="btn-secondary btn-sm disabled:opacity-40">Previous</button>
                <button disabled={page >= meta.last_page} onClick={() => setPage(p => p + 1)} className="btn-secondary btn-sm disabled:opacity-40">Next</button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Create modal */}
      <Modal open={showForm} onClose={() => setShowForm(false)} title="Add employee" size="lg">
        <EmployeeForm
          departments={depts}
          onSave={data => createMut.mutate(data)}
          onClose={() => setShowForm(false)}
        />
      </Modal>

      {/* Import modal */}
      <Modal open={showImport} onClose={() => setShowImport(false)} title="Import employees from CSV" size="md">
        <ImportModal onClose={() => setShowImport(false)} onDone={() => { setShowImport(false); qc.invalidateQueries(['employees']); }} />
      </Modal>

      {/* Edit modal */}
      <Modal open={!!editing} onClose={() => setEditing(null)} title="Edit employee" size="lg">
        {editing && (
          <EmployeeForm
            initial={editing}
            departments={depts}
            onSave={data => updateMut.mutate({ id: editing.id, data })}
            onClose={() => setEditing(null)}
          />
        )}
      </Modal>
    </div>
  );
}
